import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useObjectMemo } from "byzantine";
import ApiHttp from "byzantine/src/ApiHttp";
import { useDebounce } from "byzantine/src/dbbl/hooks/useDebouncedEffect.hook";
import {
  addDays,
  differenceInCalendarDays,
  isValid,
  parse,
  startOfDay,
  subMonths,
  subWeeks,
  subYears,
} from "date-fns";
import { useAccountsContext } from "../../contexts/AccountContext";
import { useAccountsTotal } from "../AccountTotals/hooks";
import type { SettledTransactionsState } from "../types";
import { useCurrentUserBalanceChartStorage } from "./storage";
import {
  BALANCES_REPORT_QUERY_KEY,
  DEMO_BALANCE_THRESHOLD,
  DEMO_MONTHLY_BALANCES,
  FALLBACK_TIME_PERIOD,
  HIDDEN_ACCOUNTS_REFRESH_DEBOUNCE_MS,
  TIME_PERIOD_COVERAGE_GRACE_DAYS,
  TIME_PERIODS,
} from "./constants";
import {
  TimePeriod,
  ChartDatum,
  ChartStaticData,
  ChartType,
  PeriodChange,
} from "./types";

type BalancesReportResponse = {
  report: {
    data: {
      balances: [string, string][];
    };
  };
};

const BALANCE_DATE_FORMAT = "MM/dd/yyyy";

const TICK_COUNT = 12;

const parseBalanceDate = (date: string): Date | null => {
  const parsedDate = parse(date, BALANCE_DATE_FORMAT, new Date());

  return isValid(parsedDate) ? parsedDate : null;
};

// Rolling window boundaries for the given period.
export const getWindowRange = (
  timePeriod: TimePeriod,
  now: Date
): [Date, Date] => {
  switch (timePeriod) {
    case "year":
      return [subYears(now, 1), now];
    case "quarter":
      return [subWeeks(now, TICK_COUNT), now];
    case "month":
    default:
      return [subMonths(now, 1), now];
  }
};

// Cuts chartData down to only the most recent window for the given period.
export const windowChartData = (
  chartData: ChartDatum[],
  timePeriod: TimePeriod,
  now: Date = new Date()
): ChartDatum[] => {
  const [windowStart, windowEnd] = getWindowRange(timePeriod, now);

  return chartData.filter(
    (datum) => datum.x >= windowStart && datum.x <= windowEnd
  );
};

const parseBalances = (
  balances: [string, string][] | undefined
): ChartDatum[] =>
  (balances ?? []).reduce((points, [date, amount]) => {
    const balanceDate = parseBalanceDate(date);
    const balance = Number(amount);

    if (balanceDate && Number.isFinite(balance)) {
      points.push({ x: balanceDate, y: balance });
    }

    return points;
  }, [] as ChartDatum[]);

// Timeframes which have balance history to chart
export const getAvailableTimePeriods = (
  chartData: ChartDatum[],
  now: Date
): TimePeriod[] => {
  const oldestTimestamp = chartData.reduce(
    (oldest, datum) => Math.min(oldest, datum.x.getTime()),
    Infinity
  );
  const hasEnoughPoints = chartData.length >= 2;

  return TIME_PERIODS.filter((timePeriod) => {
    if (timePeriod === FALLBACK_TIME_PERIOD) return true;
    if (!hasEnoughPoints) return false;

    const [windowStart] = getWindowRange(timePeriod, now);

    return (
      differenceInCalendarDays(new Date(oldestTimestamp), windowStart) <=
      TIME_PERIOD_COVERAGE_GRACE_DAYS
    );
  });
};

/**
 * Compares the oldest and newest balances in the window.
 *
 * Returns `null` when there aren't at least two points to compare. A zero
 * starting balance yields a `null` percentage since the change is undefined,
 * and the denominator is absolute so a negative starting balance doesn't
 * invert the direction of the trend.
 */
export const calculatePeriodChange = (
  chartData: ChartDatum[],
  chartType: ChartType
): PeriodChange | null => {
  if (chartData.length < 2) return null;

  const first = chartData[0];
  const last = chartData[chartData.length - 1];

  if (first.y === 0) {
    return { percentage: null, isPositive: true, lastPeriodDate: first.x };
  }

  const percentage = ((last.y - first.y) / Math.abs(first.y)) * 100;

  return {
    percentage,
    isPositive: chartType === "balances" ? percentage >= 0 : percentage <= 0,
    lastPeriodDate: first.x,
  };
};

/** The demo chart only has data for the monthly view. */
export const DEMO_TIME_PERIOD: TimePeriod = "month";

/**
 * Spreads the hardcoded demo balances evenly across the monthly window ending
 * on `now`, so the series always fills the chart no matter when the demo is
 * viewed and no matter how long the current month is.
 */
export const getDemoChartData = (now: Date): ChartDatum[] => {
  const [windowStart, windowEnd] = getWindowRange(DEMO_TIME_PERIOD, now);
  const span = windowEnd.getTime() - windowStart.getTime();
  const lastIndex = DEMO_MONTHLY_BALANCES.length - 1;

  return DEMO_MONTHLY_BALANCES.map((balance, index) => ({
    x: new Date(windowStart.getTime() + (span * index) / lastIndex),
    y: balance,
  }));
};

/**
 * A flat $0 series spanning the window, used when the user has activity but has
 * hidden every account so the report comes back with no balances.
 */
export const getEmptyChartData = (
  windowStart: Date,
  windowEnd: Date
): ChartDatum[] => {
  const chartData: ChartDatum[] = [];

  for (
    let date = startOfDay(windowStart);
    date <= windowEnd;
    date = addDays(date, 1)
  ) {
    chartData.push({ x: date, y: 0 });
  }

  return chartData;
};

type ChartStaticDataState = {
  staticData: ChartStaticData | undefined;
  isResolving: boolean;
  latestBalance: number | null;
  error: unknown;
};

type DefaultChartTypeState = {
  defaultChartType: ChartType;
  isResolving: boolean;
};

const buildReportUrl = (
  chartType: ChartType,
  includeHidden: boolean
): string => {
  const params = new URLSearchParams({ period: "annual" });

  if (includeHidden) {
    params.set("include_hidden", "true");
  }

  return `reports/${chartType}?${params.toString()}`;
};

/**
 * The report is always requested for the `annual` period and windowed on the
 * client, so every timeframe is served by one cached request per chart type.
 */
const useBalancesReport = (
  chartType: ChartType,
  includeHidden: boolean,
  enabled = true
) =>
  useQuery<BalancesReportResponse>({
    queryKey: [BALANCES_REPORT_QUERY_KEY, chartType, includeHidden],
    queryFn: () =>
      ApiHttp.fetch<BalancesReportResponse>(
        buildReportUrl(chartType, includeHidden)
      ),
    staleTime: 60_000,
    retry: 3,
    enabled,
  });

export const useIncludeHiddenAccounts = (): boolean => {
  const [preferences] = useCurrentUserBalanceChartStorage();

  return preferences.includeHiddenAccounts;
};

export const useChartStaticData = (
  hasSettledTransactions: boolean,
  areTransactionsLoading: boolean
): ChartStaticDataState => {
  const includeHidden = useIncludeHiddenAccounts();
  const { internalAccounts, depositAccounts } = useAccountsContext();
  const totalAvailableCash = useAccountsTotal(depositAccounts, [], true);

  const { data, isLoading, error } = useBalancesReport(
    "balances",
    includeHidden
  );

  const balances = data?.report.data.balances;
  const latestBalance = parseBalances(balances).slice(-1)[0]?.y ?? null;
  const isResolving = isLoading || areTransactionsLoading;
  // `data` only lands once the query resolves, so this can't be true mid-flight.
  const hasEmptyBalancesReport = Boolean(data) && (balances?.length ?? 0) === 0;

  const staticData = useMemo<ChartStaticData | undefined>(() => {
    if (isResolving) return undefined;

    if (internalAccounts.length === 0) {
      return "demo_no_accounts";
    }

    if (
      internalAccounts.some((account) => !account.hidden) &&
      !hasSettledTransactions &&
      totalAvailableCash < DEMO_BALANCE_THRESHOLD
    ) {
      return "demo_no_transactions";
    }

    if (hasEmptyBalancesReport) {
      return "empty";
    }

    return undefined;
  }, [
    hasEmptyBalancesReport,
    hasSettledTransactions,
    internalAccounts,
    isResolving,
    totalAvailableCash,
  ]);

  return useObjectMemo({
    staticData,
    isResolving,
    latestBalance,
    error,
  });
};

export const isDemoStaticData = (
  staticData: ChartStaticData | undefined
): boolean =>
  staticData === "demo_no_accounts" || staticData === "demo_no_transactions";

export const useHasBalanceOwedAccounts = (): boolean => {
  const { internalAccounts } = useAccountsContext();
  const includeHidden = useIncludeHiddenAccounts();

  return useMemo(
    () =>
      internalAccounts.some(
        (account) => account.isCredit() && (includeHidden || !account.hidden)
      ),
    [includeHidden, internalAccounts]
  );
};

export const useHasVisibleBalanceOwedAccounts = (): boolean => {
  const { internalAccounts } = useAccountsContext();

  return useMemo(
    () =>
      internalAccounts.some((account) => account.isCredit() && !account.hidden),
    [internalAccounts]
  );
};

export const useBalanceChartHiddenAccountsSync = (): void => {
  const { internalAccounts } = useAccountsContext();
  const queryClient = useQueryClient();

  const hiddenAccountsSignature = useMemo(
    () =>
      internalAccounts
        .filter((account) => account.hidden)
        .map((account) => account.id)
        .sort()
        .join(","),
    [internalAccounts]
  );

  const previousHiddenAccountsSignature = useRef(hiddenAccountsSignature);
  const debouncedInvalidate = useDebounce(
    () => {
      queryClient.invalidateQueries({
        queryKey: [BALANCES_REPORT_QUERY_KEY],
      });
    },
    HIDDEN_ACCOUNTS_REFRESH_DEBOUNCE_MS,
    { trailing: true }
  );

  useEffect(() => {
    // Skip invalidation if the hidden accounts signature hasn't changed.
    if (previousHiddenAccountsSignature.current === hiddenAccountsSignature) {
      return undefined;
    }

    previousHiddenAccountsSignature.current = hiddenAccountsSignature;

    debouncedInvalidate();
    return () => debouncedInvalidate.cancel();
  }, [debouncedInvalidate, hiddenAccountsSignature]);
};

export const useDefaultChartType = (
  settledTransactionsState: SettledTransactionsState,
  hasBalanceOwedAccounts: boolean
): DefaultChartTypeState => {
  const defaultChartType: ChartType = "balances";
  const includeHidden = useIncludeHiddenAccounts();
  const { data, isLoading } = useBalancesReport(
    defaultChartType,
    includeHidden
  );

  const { isLoading: areTransactionsLoading, settledTransactions } =
    settledTransactionsState;
  const referenceDate = startOfDay(new Date());
  const quarterWindowBalances = windowChartData(
    parseBalances(data?.report.data.balances),
    "quarter",
    referenceDate
  );
  const periodChange = calculatePeriodChange(
    quarterWindowBalances,
    defaultChartType
  );
  const hasOutboundTransactions = settledTransactions.some(
    (transaction) => transaction.amount < 0
  );
  const isResolving = isLoading || areTransactionsLoading;

  return useObjectMemo({
    defaultChartType:
      hasBalanceOwedAccounts &&
      !isResolving &&
      hasOutboundTransactions &&
      periodChange?.percentage !== null &&
      periodChange !== null &&
      Math.abs(periodChange.percentage) < 5
        ? "balance_owed"
        : "balances",
    isResolving,
  });
};

export const useChartData = (
  settledTransactionsState: SettledTransactionsState,
  storedChartType: ChartType | null,
  hasBalanceOwedAccounts: boolean,
  isSmallViewport: boolean,
  staticData?: ChartStaticData
) => {
  const [storedPreferences] = useCurrentUserBalanceChartStorage();

  const isDemo = isDemoStaticData(staticData);
  const normalizedStoredChartType =
    storedChartType === "balance_owed" && !hasBalanceOwedAccounts
      ? null
      : storedChartType;
  const includeHidden = useIncludeHiddenAccounts();
  const hasVisibleBalanceOwedAccounts = useHasVisibleBalanceOwedAccounts();
  const [chartType, setChartTypeState] = useState<ChartType>(
    normalizedStoredChartType ?? "balances"
  );
  // Once the user picks a chart type explicitly, the computed default must
  // never clobber that choice.
  const hasUserSelectedChartType = useRef(false);
  const previousHasVisibleBalanceOwedAccounts = useRef(
    hasVisibleBalanceOwedAccounts
  );
  const { defaultChartType, isResolving: isDefaultChartTypeLoading } =
    useDefaultChartType(settledTransactionsState, hasBalanceOwedAccounts);

  const setChartType = useCallback((type: ChartType) => {
    hasUserSelectedChartType.current = true;
    setChartTypeState(type);
  }, []);

  // If we have no non-hidden accounts pertaining to the balance owed chart, we need to switch away from it.
  useEffect(() => {
    if (
      previousHasVisibleBalanceOwedAccounts.current &&
      !hasVisibleBalanceOwedAccounts
    ) {
      setChartTypeState((currentChartType) =>
        currentChartType === "balance_owed" ? "balances" : currentChartType
      );
    }

    previousHasVisibleBalanceOwedAccounts.current =
      hasVisibleBalanceOwedAccounts;
  }, [hasVisibleBalanceOwedAccounts]);

  // If we have no accounts pertaining to the balance owed chart, we need to switch away from it.
  useEffect(() => {
    if (!hasBalanceOwedAccounts) {
      setChartTypeState((currentChartType) =>
        currentChartType === "balance_owed" ? "balances" : currentChartType
      );
    }
  }, [hasBalanceOwedAccounts]);

  useEffect(() => {
    if (normalizedStoredChartType) {
      setChartTypeState(normalizedStoredChartType);
      return;
    }

    if (
      isDemo ||
      isDefaultChartTypeLoading ||
      hasUserSelectedChartType.current
    ) {
      return;
    }

    setChartTypeState(defaultChartType);
  }, [
    defaultChartType,
    isDefaultChartTypeLoading,
    isDemo,
    normalizedStoredChartType,
  ]);

  const { data, isLoading, error } = useBalancesReport(
    chartType,
    includeHidden,
    !isDemo
  );

  // Timeframe availability always comes from the `balances` report, never the
  // active chart type: it measures how much account history exists, and the
  // balance_owed report pads days before an account's first snapshot with
  // zeros, which would fake up history the user doesn't have. This shares a
  // query key with `useChartStaticData`, so it costs no extra request — and it
  // keeps the tab set stable while a chart type switch is in flight.
  const { data: historyData } = useBalancesReport(
    "balances",
    includeHidden,
    !isDemo
  );
  const historyBalances = historyData?.report.data.balances;

  const balances = data?.report.data.balances;
  const referenceDate = startOfDay(new Date());

  const availableTimePeriods = useMemo(
    () =>
      getAvailableTimePeriods(
        parseBalances(historyBalances),
        startOfDay(new Date())
      ),
    [historyBalances]
  );

  const effectiveTimePeriod = useMemo(() => {
    // Demo data is only defined for the monthly view.
    if (isDemo) return FALLBACK_TIME_PERIOD;

    const preferredTimePeriod = isSmallViewport
      ? "quarter"
      : storedPreferences.timePeriod;

    // Until the report lands there is no history to measure, and an empty
    // series looks identical to a brand new user. Downgrading on that guess
    // would re-window the chart the moment the real data arrives.
    if (historyBalances === undefined) return preferredTimePeriod;

    // A stored timeframe the user no longer has enough history for is not
    // rewritten in storage, just ignored, so it comes back if history grows.
    return availableTimePeriods.includes(preferredTimePeriod)
      ? preferredTimePeriod
      : FALLBACK_TIME_PERIOD;
  }, [
    historyBalances,
    availableTimePeriods,
    isDemo,
    isSmallViewport,
    storedPreferences.timePeriod,
  ]);

  const [windowStart, windowEnd] = getWindowRange(
    effectiveTimePeriod,
    referenceDate
  );

  const chartData = useMemo<ChartDatum[]>(() => {
    if (isDemo) {
      return getDemoChartData(referenceDate);
    }

    const windowedChartData = windowChartData(
      parseBalances(balances),
      effectiveTimePeriod,
      referenceDate
    );

    // Only seed zeros when the report really has nothing to chart; real data
    // always wins.
    if (staticData === "empty" && windowedChartData.length === 0) {
      return getEmptyChartData(windowStart, windowEnd);
    }

    return windowedChartData;
  }, [
    balances,
    isDemo,
    referenceDate,
    staticData,
    effectiveTimePeriod,
    windowEnd,
    windowStart,
  ]);

  return useObjectMemo({
    chartType,
    setChartType,
    chartData,
    windowStart,
    windowEnd,
    availableTimePeriods,
    effectiveTimePeriod,
    // Headline amount for the chart: the most recent point in the window.
    latestBalance: chartData[chartData.length - 1]?.y ?? null,
    periodChange: calculatePeriodChange(chartData, chartType),
    isLoading: isDemo ? false : isLoading || isDefaultChartTypeLoading,
    error: isDemo ? undefined : error,
  });
};
